import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import "./ListReproductions.css";
import ListLotAnimals from '../../shared/lots/ListLotAnimals.js';
import ReproductionResultCell from './ReproductionResultCell.js';
import ReproductionStatus, { predictedDate } from './ReproductionStatus.js';
import ReproductionCardList from './ReproductionCardList.js';
import AnimalNotes from "../../shared/AnimalNotes.js";
import { daysSince, Fetch } from "../../utils/Utils.js";

// Context lets ExpandedComponent read live state without closing over
// the ListReproductions render — keeping the component reference stable.
const ReproductionContext = createContext({});

// Module-level component: its reference never changes, so react-data-table-component
// never remounts the expanded row when ListReproductions re-renders.
// showProtocol / showNotes state survives checkbox clicks.
// Day-by-day protocol progress lives entirely in the always-visible circles
// column now (ReproductionStatus, click-to-toggle) — this row just holds
// what doesn't fit there: start date, predicted IATF, and notes.
const ExpandedComponent = ({ data }) => {
  const { getProtocol, getProtocolIdForLot, getStartDateForLot, t, refreshAnimals } = useContext(ReproductionContext);
  const [showNotes, setShowNotes] = useState(false);

  // protocol_id and start_date no longer live on the animal — every animal
  // in a lot is on the same schedule as the lot's own cycle (a laggard gets
  // moved to a different lot instead of tracked with its own offset,
  // confirmed 2026-08-14), so both are always the lot's active assignment's
  // now (see BaseLot.ProtocolID's doc comment on the backend and
  // reproduction.AssignProtocol).
  const protocolId = getProtocolIdForLot(data.lot_id);
  const protocol = getProtocol(protocolId);
  const startDate = getStartDateForLot(data.lot_id);
  const predicted = predictedDate(protocol, startDate);

  return (
    <div className="ExpandedComponent">
      <div className="expanded-row-fields">
        {startDate && (
          <div className="expanded-field">
            <span className="expanded-label">{t('start_date')}</span>
            <span>{startDate}</span>
          </div>
        )}
        {predicted && (
          <div className="expanded-field">
            <span className="expanded-label">{t('predicted_iatf')}</span>
            <span>{predicted}</span>
          </div>
        )}
      </div>
      <div className="expanded-section-toggle" onClick={() => setShowNotes(v => !v)}>
        <span>{t('notes_view')}</span>
        <span className={`expanded-section-chevron${showNotes ? ' open' : ''}`}>›</span>
      </div>
      {showNotes && <AnimalNotes data={data} onNoteAdded={refreshAnimals} />}
    </div>
  );
};

export const ListReproductions = ({ initialLotFilter }) => {
  const { t } = useTranslation();
  const [protocols, setProtocols] = useState(null);
  const [completionState, setCompletionState] = useState({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [assignmentsByLotId, setAssignmentsByLotId] = useState({});

  const [actions, setActions] = useState([]);

  useEffect(() => {
    Fetch('api/data/lots.plans', 'get', null, (data) => setProtocols(data));
  }, []);

  useEffect(() => {
    Fetch('api/corral/actions', 'get', null, (data) => setActions(data || []));
  }, []);

  useEffect(() => {
    // protocol_id lives on each lot's active assignment now, not cached on
    // the animal — one bulk fetch instead of resolving it per row.
    Fetch('api/lots/assignments/active', 'GET', null, (data) => {
      const byLotId = {};
      (data || []).forEach(a => { byLotId[a.lot_id] = a; });
      setAssignmentsByLotId(byLotId);
    });
  }, [refreshTrigger]);

  // Memoize so getProtocol only changes when protocols load/change.
  const getProtocol = useMemo(
    () => (id) => (protocols || []).find(p => p._id === id) || null,
    [protocols]
  );

  const getProtocolIdForLot = useMemo(
    () => (lotId) => assignmentsByLotId[lotId]?.protocol_id,
    [assignmentsByLotId]
  );

  const getStartDateForLot = useMemo(
    () => (lotId) => assignmentsByLotId[lotId]?.start_date,
    [assignmentsByLotId]
  );

  // Context value is stable between completionState updates — only rebuilds
  // when protocols reload or the language changes. refreshAnimals lets
  // ExpandedComponent (module-level, can't close over setRefreshTrigger
  // directly) trigger a re-fetch after adding a note, same as the
  // reproduction-result dropdown already does.
  const contextValue = useMemo(
    () => ({ getProtocol, getProtocolIdForLot, getStartDateForLot, t, refreshAnimals: () => setRefreshTrigger(n => n + 1) }),
    [getProtocol, getProtocolIdForLot, getStartDateForLot, t]
  );

  // timelineFor merges this session's toggles (completionState) over the
  // server's saved step state, so the table and the mobile cards agree.
  const timelineFor = (row, protocol) => {
    const serverTimeline = row.reproduction?.protocol?.timeline_days || [];
    return protocol.timeline_days.map((_, i) => ({
      completed: completionState[`${row._id}_${i}`] ?? serverTimeline[i]?.completed ?? false,
    }));
  };
  const onStepToggled = (rowId, dayIndex, completed) =>
    setCompletionState(prev => ({ ...prev, [`${rowId}_${dayIndex}`]: completed }));

  if (protocols === null) return <>{t('loading')}...</>;

  const tableCustomStyles = {
    responsiveWrapper: { style: { overflow: 'visible' } },
  };

  const extraColumns = [
    {
      name: t('current_day'),
      // start_date lives on the lot's active assignment now, not per-animal
      // — every animal in a lot is on the same schedule as the lot's own
      // cycle (see BuildReproductionExtraSet's doc comment on the backend).
      selector: row => getStartDateForLot(row.lot_id),
      sortable: true,
      format: row => daysSince(getStartDateForLot(row.lot_id)),
    },
    {
      name: t('days_completed'),
      minWidth: '200px',
      cell: row => {
        const protocol = getProtocol(getProtocolIdForLot(row.lot_id));
        if (!protocol) return null;
        const timeline = timelineFor(row, protocol);
        return (
          <ReproductionStatus
            protocol={protocol}
            animal={{
              start_date: getStartDateForLot(row.lot_id),
              protocol:   { timeline_days: timeline },
            }}
            animalId={row._id}
            actions={actions}
            onToggle={(dayIndex, completed) => onStepToggled(row._id, dayIndex, completed)}
          />
        );
      },
    },
    {
      name: t('reproduction_result'),
      // outcome (and bull_id) come pre-resolved from GET /api/reproduction/animals
      // now — lots.reproduction_results, scoped to this tag's current cycle,
      // not a field on lots.animals.reproduction anymore (see
      // reproduction.SetOutcomeForTag).
      selector: row => row.outcome,
      sortable: true,
      cell: row => (
        <ReproductionResultCell row={row} onChanged={() => setRefreshTrigger(n => n + 1)} />
      ),
    },
  ];

  return (
    <ReproductionContext.Provider value={contextValue}>
      <div className="ListReproductions">
        <ListLotAnimals
          lotType="reproduction"
          extraColumns={extraColumns}
          expandedRowComponent={ExpandedComponent}
          initialLotFilter={initialLotFilter}
          forceRefresh={refreshTrigger}
          customStyles={tableCustomStyles}
          mobileList={(data) => (
            <ReproductionCardList
              data={data}
              getProtocol={getProtocol}
              getProtocolIdForLot={getProtocolIdForLot}
              getStartDateForLot={getStartDateForLot}
              timelineFor={timelineFor}
              onStepToggled={onStepToggled}
              actions={actions}
              onRefresh={() => setRefreshTrigger(n => n + 1)}
            />
          )}
        />
      </div>
    </ReproductionContext.Provider>
  );
};

export default ListReproductions;
